/**
 * Data Loader Utility - Dynamic imports for large data files
 * This utility helps with code splitting by loading data files only when needed
 */

// Direct imports for static data
import readingMaterialsData from "../data/reading-materials.json";
import coursesData from "../data/courses.json";
import categoriesData from "../data/categories.json";

// Cache for loaded data to avoid re-importing
const dataCache = new Map();

// Data mapping for easy access
const dataMap = {
  "reading-materials": readingMaterialsData,
  courses: coursesData,
  categories: categoriesData,
};

/**
 * Dynamically import and cache data files
 * @param {string} dataType - Type of data to load (reading-materials, courses, categories)
 * @returns {Promise<Object>} - The imported data
 */
export const loadData = async (dataType) => {
  // Return cached data if available
  if (dataCache.has(dataType)) {
    return dataCache.get(dataType);
  }

  try {
    const data = dataMap[dataType];

    if (!data) {
      throw new Error(`Unknown data type: ${dataType}`);
    }

    // Cache the loaded data
    dataCache.set(dataType, data);
    return data;
  } catch (error) {
    console.error(`Failed to load data for ${dataType}:`, error);
    throw new Error(`Failed to load ${dataType} data`);
  }
};

/**
 * Preload specific data types for better performance
 * @param {string[]} dataTypes - Array of data types to preload
 */
export const preloadData = async (dataTypes) => {
  const promises = dataTypes.map((type) => loadData(type));

  try {
    await Promise.all(promises);
    console.log("Data preloaded successfully:", dataTypes);
  } catch (error) {
    console.warn("Some data failed to preload:", error);
  }
};

/**
 * Clear data cache (useful for development or memory management)
 */
export const clearDataCache = () => {
  dataCache.clear();
};

/**
 * Get cache status for debugging
 */
export const getCacheStatus = () => {
  return {
    cachedTypes: Array.from(dataCache.keys()),
    cacheSize: dataCache.size,
  };
};

/**
 * Simple data loader for components - returns a promise
 * For React hook version, import from './useDataLoader'
 * @param {string} dataType - Type of data to load
 * @returns {Promise<Object>} - Promise that resolves to the data
 */
export const loadDataForComponent = (dataType) => {
  return loadData(dataType);
};
